// src/components/home.jsx
import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import {
  AssignmentTurnedInOutlined,
  BusinessCenterOutlined,
  GroupsOutlined,
  PhotoLibraryOutlined,
} from '@mui/icons-material';
import { CLIENT_PROFILES, SERVICES, WORKFLOW_STEPS } from './public/publicSiteData';
import { getPublicPageDefinition } from './public/publicSiteRegistry.mjs';
import {
  ArrowIcon,
  CheckList,
  CtaBand,
  PublicHero,
  PublicSeo,
  SectionHeading,
  ServiceIcon,
  WorkflowCard,
} from './public/PublicUi';

const AnimatedHowItWorks = lazy(() => import('./animatedHowItWorks'));
const GetInTouch = lazy(() => import('./getInTouch'));
const PAGE = getPublicPageDefinition('/');

function SectionFallback({ label }) {
  return (
    <div className="ts-loading-state" role="status" aria-live="polite">
      {label}
    </div>
  );
}

export default function Home() {
  return (
    <div className="ts-public-page ts-page--home">
      <PublicSeo page={PAGE} />

      <PublicHero page={PAGE}>
        <WorkflowCard />
      </PublicHero>

      <section className="ts-section ts-modes" aria-label="Ways to work with TaskerSys">
        <div className="ts-container">
          <SectionHeading
            eyebrow="TWO WAYS TO WORK TOGETHER"
            title="Choose the support your property team needs."
          >
            <p>
              Use TaskerSys as an operations platform, engage our field teams for a defined
              assignment, or connect both around the same scope. Each offering can stand on
              its own.
            </p>
          </SectionHeading>

          <div className="ts-mode-grid">
            <article className="ts-mode">
              <span className="ts-mode-tag">01 / FIELD SERVICES</span>
              <h3>Add execution capacity.</h3>
              <p>
                Bring us a defined property scope: a vacant apartment, repair backlog,
                cleaning assignment, common-area program, or planned renovation phase.
              </p>
              <CheckList
                items={[
                  'A defined scope, approval path, and coordinated access',
                  'Progress updates, photographs, and issue visibility',
                  'A contained pilot before a broader rollout',
                  'Commercial terms matched to the actual work',
                ]}
              />
              <Link className="ts-text-link" to="/services">
                Explore field services <ArrowIcon />
              </Link>
            </article>

            <article className="ts-mode ts-mode--blue">
              <span className="ts-mode-tag">02 / OPERATIONS PLATFORM</span>
              <h3>Keep the work connected.</h3>
              <p>
                Give managers, owners, service providers, dispatchers, subcontractors,
                and technicians a shared operating record from request through closeout.
              </p>
              <CheckList
                items={[
                  'Service requests, tickets, projects, and schedules',
                  'Roles, permissions, buildings, vendors, and field teams',
                  'Photos, comments, approvals, budgets, and completion records',
                  'Financial workflow and reporting across active work',
                ]}
              />
              <Link className="ts-text-link" to="/platform">
                Explore the platform <ArrowIcon />
              </Link>
            </article>
          </div>
        </div>
      </section>

      <section className="ts-section ts-section--soft" aria-label="Best-fit TaskerSys clients">
        <div className="ts-container">
          <SectionHeading
            eyebrow="WHERE WE FIT BEST"
            title="Additional capacity when the workload and approval path are clear."
            linkTo="/about"
            linkLabel="How TaskerSys fits into existing operations"
          >
            <p>
              The strongest fit is an active property organization with identifiable work,
              an accessible decision maker, and a workable approval and payment path.
            </p>
          </SectionHeading>

          <div className="ts-capabilities">
            {CLIENT_PROFILES.map((profile, index) => {
              const icons = [BusinessCenterOutlined, GroupsOutlined, AssignmentTurnedInOutlined];
              const Icon = icons[index] || BusinessCenterOutlined;

              return (
                <article key={profile.title}>
                  <Icon aria-hidden="true" />
                  <h3>{profile.title}</h3>
                  <p>{profile.text}</p>
                  <p className="ts-card-note">{profile.signal}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ts-section" id="services-section" aria-label="TaskerSys field services">
        <div className="ts-container">
          <SectionHeading
            eyebrow="TASKERSYS FIELD SERVICES"
            title="Choose the service closest to the assignment."
            linkTo="/services"
            linkLabel="All field services"
          >
            <p>
              Each service page explains the typical scope, information needed for review,
              and the commercial structures that may fit the assignment.
            </p>
          </SectionHeading>

          <div className="ts-service-list">
            {SERVICES.map((service, index) => (
              <article className="ts-service-row" key={service.slug}>
                <span className="ts-service-number" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="ts-service-icon">
                  <ServiceIcon slug={service.slug} />
                </div>
                <h3>
                  <Link to={`/services/${service.slug}`}>{service.shortTitle}</Link>
                </h3>
                <p>{service.lead}</p>
                <Link
                  className="ts-arrow-link"
                  to={`/services/${service.slug}`}
                  aria-label={`Explore ${service.shortTitle}`}
                >
                  <ArrowIcon />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ts-section ts-section--soft" aria-label="TaskerSys working process">
        <div className="ts-container">
          <SectionHeading
            eyebrow="ONE SCOPE, ONE OPERATING RECORD"
            title="Less uncertainty at every handoff."
          >
            <p>
              Management, coordination, field execution, approvals, and closeout stay connected
              while the assignment moves from definition to completion.
            </p>
          </SectionHeading>

          <div className="ts-platform-workflow">
            <Suspense fallback={<SectionFallback label="Loading workflow illustration…" />}>
              <AnimatedHowItWorks />
            </Suspense>

            <ol className="ts-process ts-process--four">
              {WORKFLOW_STEPS.map((step) => (
                <li key={step.number}>
                  <span className="ts-process-index">{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="ts-section ts-section--compact" aria-labelledby="home-projects-title">
        <div className="ts-container ts-project-bridge">
          <div className="ts-project-emblem" aria-hidden="true">
            <PhotoLibraryOutlined />
          </div>
          <div>
            <p className="ts-eyebrow">FIELD ACTIVITY &amp; PROJECTS</p>
            <h2 id="home-projects-title">See the work in context.</h2>
            <p>
              Browse the current TaskerSys gallery for remodeling, turnover, furnishing,
              cleaning, and other field-service categories.
            </p>
          </div>
          <Link className="ts-button ts-button--outline" to="/projects">
            View project gallery <ArrowIcon />
          </Link>
        </div>
      </section>

      <section
        className="ts-section ts-section--soft"
        id="home-contact"
        aria-labelledby="home-contact-title"
      >
        <div className="ts-container ts-contact-form-layout">
          <div>
            <p className="ts-eyebrow">START A CONVERSATION</p>
            <h2 id="home-contact-title">Tell us what needs to move next.</h2>
            <p>
              Share a vacancy, punchlist, repair backlog, common-area program, renovation
              phase, cleaning need, or platform workflow. A clear first message makes the
              next step easier to define.
            </p>
            <CheckList
              items={[
                'Property address or service area',
                'Scope, approximate quantity, and current condition',
                'Photographs, punchlists, drawings, or supporting notes',
                'Target timing, access requirements, and approval contact',
              ]}
            />
          </div>

          <Suspense fallback={<SectionFallback label="Loading inquiry form…" />}>
            <GetInTouch />
          </Suspense>
        </div>
      </section>

      <CtaBand
        eyebrow="START WITH ONE CLEAR SCOPE"
        title="Define the first assignment, then expand only when it works."
        text="Tell us about the property, the work required, and the target timing. We can discuss the most practical next step."
        buttonLabel="Tell us what you need"
      />
    </div>
  );
}

